import React, { useEffect, useState } from "react";
import Popup from "components/pop-up/pop-up";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import {
  addReferralDecision,
  fetchProsecutors,
} from "services/assetRecoveryService";
import { useParams } from "react-router-dom";
import { FileUpload } from "components/form/FileUpload";
import { AssetProsecutor, LoginAccount } from "types";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { login } from "services/authService";
import { useAuth } from "context/AuthContext";

interface DecisionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  onDecsionCreated: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

const DecisionPopup: React.FC<DecisionPopupProps> = ({
  isOpen,
  onClose,
  referralId,
  onDecsionCreated,
  setActiveTab,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [decisionType, setDecisionType] = useState<string>("");
  const [decisionSummary, setDecisionSummary] = useState<string>("");
  const [decisionDate, setDecisionDate] = useState<string>("");
  const [decisionDocument, setDecisionDocument] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [selectedProsecutor, setSelectedProsecutor] = useState("");
  const [prosecutors, setProsecutors] = useState<AssetProsecutor[]>([]);
  const { loginData } = useAuth();
  useEffect(() => {
    fetchProsecutors(loginData as LoginAccount)
      .then(setProsecutors)
      .catch(console.error);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Find the selected prosecutor object
      const selectedProsecutorObj = prosecutors.find(
        (pros) => pros.user?.id === selectedProsecutor,
      );

      // Get the prosecutor's full name
      const prosecutorName = selectedProsecutorObj
        ? `${selectedProsecutorObj.user?.first_name} ${selectedProsecutorObj.user?.last_name}`
        : "";

      // Prepare form data
      const formData = new FormData();
      formData.append("referral", referralId ?? id);
      formData.append("decision_type", decisionType);
      formData.append("decision_summary", decisionSummary);
      formData.append("decision_date", decisionDate);

      if (decisionDocument.length > 0) {
        decisionDocument.forEach((file) => {
          formData.append("decision_document", file);
        });
      }

      // Set status based on decision type
      if (decisionType === "assign") {
        formData.append("action_status", "Investigation Ongoing");
        formData.append("forward_status", "Assigned to Prosecutor");
        formData.append("assigned_prosecutor", prosecutorName);
        formData.append("assigned_prosecutor_user_id", selectedProsecutor);
      } else if (decisionType === "close") {
        formData.append("action_status", "Closed by cordinator");
        // formData.append("forward_status", "completed");
      }

      const response = await addReferralDecision({
        formData,
        referral: referralId ?? id,
      });
      if (onDecsionCreated && response) {
        onDecsionCreated(response);
      }
      setActiveTab?.("Decision");

      showTooltip(t("decision_submitted_successfully"), "success");
      onClose();
      resetForm();
    } catch (error) {
      showTooltip(t("failed_to_submit_decision"), "error");
    } finally {
      setLoading(false);
    }
  };
  const resetForm = () => {
    setDecisionType("");
    setDecisionSummary("");
    setDecisionDate("");
    setDecisionDocument([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Popup isOpen={isOpen}>
      <div className="referral-details-popup-container">
        <div className="pop-up-form">
          <div className="form-header flex justify-between items-center">
            <div>
              <p className="referral-popup-title">Add Decision</p>
              <p className="description">Make a decision for this referral</p>
            </div>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={handleClose}
              className="cursor-pointer"
            >
              <path
                d="M16.95 8.46404C17.1322 8.27544 17.233 8.02283 17.2307 7.76064C17.2284 7.49844 17.1232 7.24763 16.9378 7.06222C16.7524 6.87681 16.5016 6.77164 16.2394 6.76936C15.9772 6.76709 15.7246 6.86788 15.536 7.05004L12 10.586L8.464 7.05004C8.37175 6.95453 8.26141 6.87835 8.13941 6.82594C8.0174 6.77353 7.88618 6.74594 7.7534 6.74479C7.62062 6.74363 7.48894 6.76894 7.36605 6.81922C7.24315 6.8695 7.1315 6.94375 7.03761 7.03764C6.94371 7.13154 6.86946 7.24319 6.81918 7.36609C6.7689 7.48898 6.7436 7.62066 6.74475 7.75344C6.7459 7.88622 6.77349 8.01744 6.8259 8.13944C6.87831 8.26145 6.95449 8.37179 7.05 8.46404L10.586 12L7.05 15.536C6.95449 15.6283 6.87831 15.7386 6.8259 15.8606C6.77349 15.9826 6.7459 16.1139 6.74475 16.2466C6.7436 16.3794 6.7689 16.5111 6.81918 16.634C6.86946 16.7569 6.94371 16.8685 7.03761 16.9624C7.1315 17.0563 7.24315 17.1306 7.36605 17.1809C7.48894 17.2311 7.62062 17.2564 7.7534 17.2553C7.88618 17.2541 8.0174 17.2265 8.13941 17.1741C8.26141 17.1217 8.37175 17.0456 8.464 16.95L12 13.414L15.536 16.95C15.6282 17.0456 15.7386 17.1217 15.8606 17.1741C15.9826 17.2265 16.1138 17.2541 16.2466 17.2553C16.3794 17.2564 16.5111 17.2311 16.634 17.1809C16.7569 17.1306 16.8685 17.0563 16.9624 16.9624C17.0563 16.8685 17.1305 16.7569 17.1808 16.634C17.2311 16.5111 17.2564 16.3794 17.2553 16.2466C17.2541 16.1139 17.2265 15.9826 17.1741 15.8606C17.1217 15.7386 17.0455 15.6283 16.95 15.536L13.414 12L16.95 8.46404Z"
                fill="black"
              />
            </svg>
          </div>

          <div>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col">
                <label className="referral-popup-label">Decision Type *</label>
                <select
                  className="referral-form-field"
                  value={decisionType}
                  onChange={(e) => setDecisionType(e.target.value)}
                  required
                >
                  <option value="">Select Decision Type</option>
                  <option value="assign">Assign</option>
                  <option value="close">Close</option>
                </select>
              </div>
              {decisionType === "close" && (
                <>
                  <div className="flex flex-col mt-4">
                    <label className="referral-popup-label">
                      Decision Summary *
                    </label>
                    <textarea
                      className="referral-form-field h-24"
                      placeholder="Enter decision summary"
                      value={decisionSummary}
                      onChange={(e) => setDecisionSummary(e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex flex-col mt-4">
                    <label className="referral-popup-label">
                      Decision Date *
                    </label>
                    <input
                      type="date"
                      className="referral-form-field"
                      value={decisionDate}
                      onChange={(e) => setDecisionDate(e.target.value)}
                      required
                    />
                  </div>

                  <MultipleFileUploadComponent
                    uploadedFiles={decisionDocument}
                    onFilesChange={setDecisionDocument}
                  />
                </>
              )}
              {decisionType === "assign" && (
                <div>
                  <div className="flex flex-col">
                    <label className="referral-popup-label">Assign To: </label>
                    <select
                      className="referral-form-field"
                      value={selectedProsecutor}
                      onChange={(e) => setSelectedProsecutor(e.target.value)}
                      required
                    >
                      <option value="">Select Prosecutor</option>
                      {prosecutors?.map((pros) => (
                        <option value={pros?.user?.id} key={pros?.user?.id}>
                          {pros.user?.first_name} {pros.user?.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="mt-6 mb-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`referral-form-button text-white w-full ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? "Submitting..." : "Submit Decision"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default DecisionPopup;
